<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden policy-ai-triage-page">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">
        AI Triage
      </span>
    </div>
    <div class="a-body flex-grow-1 p-20-px overflow-y-auto">
      <div class="policy-ai-triage-content">
        <a-spin v-if="fqdnsLoading" />

        <template v-else-if="fqdnRows.length === 0">
          <a-alert type="warning" show-icon message="No hosts found yet." />
          <p class="text-muted fs-12 mt-2 mb-0">
            Hosts are loaded from your FQDN data. Add a monitoring URL under Webpages; URLs map to hosts automatically (you cannot add a domain from this screen).
          </p>
        </template>

        <template v-else>
          <div class="policy-host-picker mb-4">
            <div class="policy-picker-group">
              <span class="policy-picker-group__label">
                Browse by host
              </span>
              <a-select
                v-model="selectedHostKey"
                show-search
                option-filter-prop="children"
                placeholder="Select a host"
                class="policy-host-select policy-picker-control"
                :dropdown-match-select-width="false"
                @change="onHostSelectChange"
              >
                <a-select-option v-for="row in fqdnRows" :key="tabRowKey(row)" :value="tabRowKey(row)">
                  {{ row.hostname }}
                </a-select-option>
              </a-select>
            </div>

            <div class="policy-host-picker__divider" />

            <div class="policy-picker-group">
              <span class="policy-picker-group__label">
                Jump to URL
              </span>
              <div class="policy-host-jump-wrap">
                <a-input
                  v-model="jumpToUrlInput"
                  class="policy-host-jump-input policy-picker-control"
                  placeholder="Paste URL to find its host"
                  @pressEnter="onGoToDomainByUrl"
                />
                <a-button type="primary" class="policy-host-jump-btn" @click="onGoToDomainByUrl">
                  Go
                </a-button>
              </div>
            </div>
          </div>

          <template v-if="selectedRow">
            <a-spin v-if="loadingDetails[selectedHostKey]" />

            <template v-else-if="triageLoadError[selectedHostKey]">
              <a-alert type="error" show-icon message="Could not load AI Triage settings for this host." />
              <div class="mt-2">
                <a-button type="primary" size="small" @click="retryTriageForSelectedHost">
                  Retry
                </a-button>
              </div>
            </template>

            <template v-else-if="triageByTabKey[selectedHostKey]">
              <a-tabs type="card" :activeKey="activeTabKey" @change="onTabChange">
                <a-tab-pane :key="tabs.domainUrls" tab="URLs under this domain">
                  <AiTriageDomainUrlsPanel
                    :rows="urlsForRow(selectedRow)"
                    :loading="Boolean(urlsLoadingDetails[selectedHostKey])"
                    :load-error="Boolean(urlsLoadError[selectedHostKey])"
                    :highlighted-url="highlightedWebsiteUrl"
                    @retry="retryUrlsForSelectedHost"
                  />
                </a-tab-pane>
                <a-tab-pane v-if="canShowPreferencesTab" :key="tabs.preferences" tab="AI Triage Preferences">
                  <AiTriageRulesWorkspace
                    ref="aiTriageWorkspace"
                    :key="selectedHostKey"
                    :fqdn-id="selectedRow.fqdn_id"
                    :storage-scope-key="storageScopeKeyForRow(selectedRow)"
                    :storage-scope-is-domain="true"
                    :hide-storage-scope-notice="true"
                    :preferences-section-enabled="isFqdnAiTriagePreferencesFeatureEnabled"
                    :prompt-section-enabled="isFqdnAiTriagePromptFeatureEnabled"
                    :advanced-prompt-ui-enabled="isFqdnAiTriagePromptFeatureEnabled"
                    :configurable-items="aiTriageConfigurableItemsForRow(selectedRow)"
                    :server-ai-triage="triageByTabKey[selectedHostKey].ai_triage"
                    :saving-preferences="aiTriageSaving"
                    :saving-prompt="aiTriageSavingPrompt"
                    @save-preferences="onAiTriageSavePreferences"
                    @save-prompt="onAiTriageSavePrompt"
                  />
                </a-tab-pane>
                <a-tab-pane v-if="canShowWhitelistedTab" :key="tabs.whitelistedDomains" tab="Whitelisted Domains">
                  <AiTriageWhitelistedDomainsPanel
                    :domains="whitelistedDomainsForRow(selectedRow)"
                    :all-domains="allWhitelistedDomainsForUser"
                    :saving="aiTriageSavingWhitelist"
                    @save-whitelisted-domains="onAiTriageSaveWhitelistedDomains"
                  />
                </a-tab-pane>
              </a-tabs>
            </template>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import AiTriageRulesWorkspace from '@modules/Webpages/components/AiTriageRulesWorkspace.vue';
import AiTriageWhitelistedDomainsPanel from './AiTriageWhitelistedDomainsPanel.vue';
import AiTriageDomainUrlsPanel from './AiTriageDomainUrlsPanel.vue';
import WebpageService from '@modules/Webpages/services/webpage.service';
import { AI_TRIAGE_CONFIG_ITEMS_EXAMPLE, AI_TRIAGE_PAGE_TABS } from '../consts/aiTriage.consts';
import {
  extractHostname as extractHostnameUtil,
  normalizeComparableUrl as normalizeComparableUrlUtil,
  normalizeDomainList as normalizeDomainListUtil,
  normalizeHostname as normalizeHostnameUtil,
} from '../utils/domain.utils';

export default {
  name: 'AiTriageHostPage',
  components: {
    AiTriageRulesWorkspace,
    AiTriageWhitelistedDomainsPanel,
    AiTriageDomainUrlsPanel,
  },
  data() {
    return {
      fqdnRows: [],
      fqdnsLoading: true,
      selectedHostKey: '',
      jumpToUrlInput: '',
      highlightedWebsiteUrl: '',
      triageByTabKey: {},
      urlsByTabKey: {},
      triageLoadError: {},
      urlsLoadError: {},
      loadingDetails: {},
      urlsLoadingDetails: {},
      activeTabKey: AI_TRIAGE_PAGE_TABS.domainUrls,
      tabs: AI_TRIAGE_PAGE_TABS,
      aiTriageSaving: false,
      aiTriageSavingPrompt: false,
      aiTriageSavingWhitelist: false,
      triageLoadPromises: {},
    };
  },
  computed: {
    featureAccess() {
      return (this.$store.state.user.data && this.$store.state.user.data.feature_access) || {};
    },
    isFqdnAiTriagePromptFeatureEnabled() {
      return Boolean(this.featureAccess['fqdn-ai-triage-prompt']);
    },
    isFqdnAiTriagePreferencesFeatureEnabled() {
      return Boolean(this.featureAccess['fqdn-ai-triage-preferences']);
    },
    isAiTriageRulesFeatureEnabled() {
      return Boolean(this.featureAccess['alert-triage']);
    },
    canShowPreferencesTab() {
      return this.isFqdnAiTriagePreferencesFeatureEnabled || this.isFqdnAiTriagePromptFeatureEnabled;
    },
    canShowWhitelistedTab() {
      return this.isAiTriageRulesFeatureEnabled;
    },
    availableTabKeys() {
      const keys = [this.tabs.domainUrls];
      if (this.canShowPreferencesTab) {
        keys.push(this.tabs.preferences);
      }
      if (this.canShowWhitelistedTab) {
        keys.push(this.tabs.whitelistedDomains);
      }
      return keys;
    },
    selectedRow() {
      return this.rowFromTabKey(this.selectedHostKey);
    },
    allWhitelistedDomainsForUser() {
      return this.normalizeWhitelistedDomains(
        this.fqdnRows.flatMap((row) => this.whitelistedDomainsForRow(row)),
      );
    },
  },
  mounted() {
    this.loadFqdnList();
  },
  watch: {
    availableTabKeys() {
      this.ensureValidActiveTab();
    },
  },
  methods: {
    ensureValidActiveTab() {
      if (!this.availableTabKeys.includes(this.activeTabKey)) {
        this.activeTabKey = this.tabs.domainUrls;
      }
    },
    async onTabChange(tabKey) {
      if (!this.availableTabKeys.includes(tabKey)) {
        return;
      }
      this.activeTabKey = tabKey;
      if (tabKey === this.tabs.domainUrls && this.selectedHostKey) {
        await this.ensureUrlsForTab(this.selectedHostKey);
      }
    },
    extractHostname(rawUrl) {
      return extractHostnameUtil(rawUrl);
    },
    normalizeHostname(value) {
      return normalizeHostnameUtil(value);
    },
    urlMatchesSelectedHost(rawUrl, hostName) {
      const host = this.extractHostname(rawUrl);
      const target = this.normalizeHostname(hostName);
      if (!host || !target) {
        return false;
      }
      return host === target || host.endsWith(`.${target}`);
    },
    urlsForRow(row) {
      if (!row) {
        return [];
      }
      return this.urlsByTabKey[this.tabRowKey(row)] || [];
    },
    tabRowKey(row) {
      if (row.fqdn_id != null && row.fqdn_id !== '') {
        return `f-${row.fqdn_id}`;
      }
      const host = row.hostname ? String(row.hostname).toLowerCase().replace(/[^a-z0-9.-]+/g, '-') : 'host';
      const i = this.fqdnRows.indexOf(row);
      return `h-${host}-${i >= 0 ? i : 0}`;
    },
    storageScopeKeyForRow(row) {
      const key = this.tabRowKey(row);
      const pack = this.triageByTabKey[key];
      const ai = pack && pack.ai_triage;
      if (ai && typeof ai.scope_key === 'string' && ai.scope_key.trim()) {
        return ai.scope_key.trim();
      }
      if (row.hostname) {
        return `fqdn:${String(row.hostname).toLowerCase()}`;
      }
      return '';
    },
    normalizeWhitelistedDomains(domains) {
      return normalizeDomainListUtil(domains);
    },
    whitelistedDomainsForRow(row) {
      if (!row) {
        return [];
      }
      if (Array.isArray(row.whitelisted_domains)) {
        return this.normalizeWhitelistedDomains(row.whitelisted_domains);
      }
      const pack = this.triageByTabKey[this.tabRowKey(row)];
      const fromPack = pack && pack.ai_triage && pack.ai_triage.whitelisted_domains;
      return Array.isArray(fromPack) ? this.normalizeWhitelistedDomains(fromPack) : [];
    },
    applyWhitelistedDomainsFromPack(row, pack) {
      if (!row || !pack || !pack.ai_triage || typeof pack.ai_triage !== 'object') {
        return;
      }
      const domains = Array.isArray(pack.ai_triage.whitelisted_domains)
        ? this.normalizeWhitelistedDomains(pack.ai_triage.whitelisted_domains)
        : [];
      this.$set(pack.ai_triage, 'whitelisted_domains', domains);
      this.$set(row, 'whitelisted_domains', domains);
    },
    aiTriageConfigurableItemsForRow(row) {
      const pack = this.triageByTabKey[this.tabRowKey(row)];
      const items = pack && pack.ai_triage_config_items;
      if (Array.isArray(items)) {
        return items;
      }
      return AI_TRIAGE_CONFIG_ITEMS_EXAMPLE;
    },
    async loadFqdnList() {
      this.fqdnsLoading = true;
      try {
        const list = await WebpageService.fetchAiTriageFqdns();
        this.fqdnRows = Array.isArray(list) ? list : [];
        if (this.fqdnRows.length > 0) {
          const first = this.fqdnRows[0];
          this.selectedHostKey = this.tabRowKey(first);
          await Promise.all([
            this.ensureTriageForTab(this.selectedHostKey),
            this.ensureUrlsForTab(this.selectedHostKey),
          ]);
        }
      } catch (e) {
        this.$showErrorMessage('Could not load host list.');
      } finally {
        this.fqdnsLoading = false;
      }
    },
    async onHostSelectChange(key, options = {}) {
      const { preserveHighlight = false } = options;
      if (!preserveHighlight) {
        this.highlightedWebsiteUrl = '';
      }
      await Promise.all([
        this.ensureTriageForTab(key),
        this.ensureUrlsForTab(key),
      ]);
    },
    rowFromTabKey(tabKey) {
      return this.fqdnRows.find((r) => this.tabRowKey(r) === tabKey) || null;
    },
    findHostRowByUrl(rawUrl) {
      const host = this.extractHostname(rawUrl);
      if (!host) {
        return null;
      }
      const exact = this.fqdnRows.find((row) => this.normalizeHostname(row.hostname) === host);
      if (exact) {
        return exact;
      }
      const candidates = this.fqdnRows.filter((row) => {
        const rowHost = this.normalizeHostname(row.hostname);
        return rowHost && (host.endsWith(`.${rowHost}`) || rowHost.endsWith(`.${host}`));
      });
      if (!candidates.length) {
        return null;
      }
      // Prefer the most specific hostname match.
      return candidates.sort((a, b) => String(b.hostname || '').length - String(a.hostname || '').length)[0];
    },
    async onGoToDomainByUrl() {
      const input = String(this.jumpToUrlInput || '').trim();
      if (!input) {
        this.$showErrorMessage('Please input a URL first.');
        return;
      }
      const row = this.findHostRowByUrl(input);
      if (!row) {
        this.$showErrorMessage('No matching host/domain found for this URL.');
        return;
      }
      const key = this.tabRowKey(row);
      this.selectedHostKey = key;
      this.activeTabKey = this.tabs.domainUrls;
      this.highlightedWebsiteUrl = normalizeComparableUrlUtil(input);
      await this.onHostSelectChange(key, { preserveHighlight: true });
    },
    redirectOnNoAccess() {
      this.$router.replace({ path: '/home' });
    },
    /** Not allowed or wrong session for this API — send user away without noisy errors. */
    isNoAccessError(error) {
      const status = error && error.response && error.response.status;
      return status === 401 || status === 403;
    },
    async retryTriageForSelectedHost() {
      if (!this.selectedHostKey) {
        return;
      }
      await this.ensureTriageForTab(this.selectedHostKey, true);
    },
    async retryUrlsForSelectedHost() {
      if (!this.selectedHostKey) {
        return;
      }
      await this.ensureUrlsForTab(this.selectedHostKey, true);
    },
    async ensureUrlsForTab(tabKey, force = false) {
      const row = this.rowFromTabKey(tabKey);
      if (!row || !row.hostname) {
        return;
      }
      if (force && this.urlsByTabKey[tabKey]) {
        this.$delete(this.urlsByTabKey, tabKey);
      }
      if (!force && this.urlsByTabKey[tabKey]) {
        return;
      }
      this.$set(this.urlsLoadingDetails, tabKey, true);
      this.$set(this.urlsLoadError, tabKey, false);
      try {
        const list = await WebpageService.getSimpleURLList({
          q: row.hostname,
          itemsPerPage: 200,
        });
        const rows = Array.isArray(list) ? list : [];
        const filtered = rows
          .filter((item) => this.urlMatchesSelectedHost(item && item.url_name, row.hostname))
          .sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
        this.$set(this.urlsByTabKey, tabKey, filtered);
        this.$set(this.urlsLoadError, tabKey, false);
      } catch (e) {
        this.$set(this.urlsLoadError, tabKey, true);
      } finally {
        this.$set(this.urlsLoadingDetails, tabKey, false);
      }
    },
    async ensureTriageForTab(tabKey, force = false) {
      const row = this.rowFromTabKey(tabKey);
      if (!row || row.fqdn_id == null) {
        return;
      }
      if (force && this.triageByTabKey[tabKey]) {
        this.$delete(this.triageByTabKey, tabKey);
      }
      if (force && this.triageLoadPromises[tabKey]) {
        this.$delete(this.triageLoadPromises, tabKey);
      }
      if (!force && this.triageByTabKey[tabKey]) {
        return;
      }
      if (!force && this.triageLoadPromises[tabKey]) {
        return this.triageLoadPromises[tabKey];
      }

      const loadPromise = (async () => {
        this.$set(this.loadingDetails, tabKey, true);
        this.$set(this.triageLoadError, tabKey, false);
        try {
          const full = await WebpageService.getAiTriageForFqdn(row.fqdn_id);
          if (full && typeof full === 'object' && full.ai_triage) {
            const pack = { ...full };
            this.applyWhitelistedDomainsFromPack(row, pack);
            this.$set(this.triageByTabKey, tabKey, pack);
            this.$set(this.triageLoadError, tabKey, false);
          } else {
            this.$set(this.triageLoadError, tabKey, true);
            this.$showErrorMessage('Could not load data for this host.');
          }
        } catch (e) {
          this.$set(this.triageLoadError, tabKey, true);
          this.$showErrorMessage('Could not load data for this host.');
          throw e;
        } finally {
          this.$set(this.loadingDetails, tabKey, false);
        }
      })();

      this.$set(this.triageLoadPromises, tabKey, loadPromise);
      try {
        await loadPromise;
      } finally {
        this.$delete(this.triageLoadPromises, tabKey);
      }
    },
    isWhitelistApiUnavailable(error) {
      const status = error && error.response && error.response.status;
      return status === 404 || status === 405 || status === 409 || status === 501;
    },
    setWhitelistedDomainsDraftForRow(row, domains) {
      const tabKey = this.tabRowKey(row);
      const existingPack = this.triageByTabKey[tabKey] || {};
      if (!existingPack.ai_triage || typeof existingPack.ai_triage !== 'object') {
        this.$set(existingPack, 'ai_triage', {});
      }
      this.$set(existingPack.ai_triage, 'whitelisted_domains', domains);
      this.$set(this.triageByTabKey, tabKey, existingPack);
      this.$set(row, 'whitelisted_domains', domains);
    },
    applyServerSnapshotToRef(tabKey, aiTriage) {
      if (tabKey !== this.selectedHostKey) {
        return;
      }
      const panel = this.$refs.aiTriageWorkspace;
      const inst = Array.isArray(panel) ? panel[0] : panel;
      if (inst && typeof inst.applyFromServer === 'function') {
        inst.lastAppliedAiTriageScopeKey = null;
        inst.applyFromServer(aiTriage);
      }
    },
    async onAiTriageSavePreferences(payload) {
      const fid = payload && payload.fqdnId;
      if (fid == null) {
        return;
      }
      this.aiTriageSaving = true;
      try {
        const aiTriage = await WebpageService.putAiTriagePreferencesByFqdn(fid, {
          itemLevels: payload.itemLevels,
        });
        const row = this.fqdnRows.find((r) => r.fqdn_id === Number(fid) || r.fqdn_id === fid);
        if (row) {
          const tabKey = this.tabRowKey(row);
          if (this.triageByTabKey[tabKey]) {
            this.$set(this.triageByTabKey[tabKey], 'ai_triage', aiTriage);
          }
          this.$nextTick(() => {
            this.applyServerSnapshotToRef(tabKey, aiTriage);
          });
        }
        this.$showSuccessMessage('Saved.');
      } catch (error) {
        if (this.isNoAccessError(error)) {
          this.redirectOnNoAccess();
        } else {
          this.$showMultiErrorMessage(this.axiosErrorMessages(error));
        }
      } finally {
        this.aiTriageSaving = false;
      }
    },
    async onAiTriageSavePrompt(payload) {
      const fid = payload && payload.fqdnId;
      if (fid == null) {
        return;
      }
      this.aiTriageSavingPrompt = true;
      try {
        const aiTriage = await WebpageService.putAiTriagePromptByFqdn(fid, {
          draft_content: payload.draft_content,
        });
        const row = this.fqdnRows.find((r) => r.fqdn_id === Number(fid) || r.fqdn_id === fid);
        if (row) {
          const tabKey = this.tabRowKey(row);
          if (this.triageByTabKey[tabKey]) {
            this.$set(this.triageByTabKey[tabKey], 'ai_triage', aiTriage);
          }
          this.$nextTick(() => {
            this.applyServerSnapshotToRef(tabKey, aiTriage);
          });
        }
        this.$showSuccessMessage('Saved.');
      } catch (error) {
        if (this.isNoAccessError(error)) {
          this.redirectOnNoAccess();
        } else {
          this.$showMultiErrorMessage(this.axiosErrorMessages(error));
        }
      } finally {
        this.aiTriageSavingPrompt = false;
      }
    },
    async onAiTriageSaveWhitelistedDomains(payload) {
      const row = this.selectedRow;
      if (!row || row.fqdn_id == null) {
        return;
      }
      const applyToAllDomains = Boolean(payload && payload.applyToAllDomains);
      if (applyToAllDomains) {
        const mode = payload && payload.mode === 'add-only' ? 'add' : 'remove';
        const rawDomains = mode === 'add'
          ? (payload.deltaDomains || [])
          : (payload.removedDomains || []);
        await this.applyWhitelistBulkMode(mode, rawDomains);
        return;
      }
      const domains = this.normalizeWhitelistedDomains(payload && payload.domains);
      const tabKey = this.tabRowKey(row);
      const previousDomains = this.whitelistedDomainsForRow(row);

      this.setWhitelistedDomainsDraftForRow(row, domains);

      this.aiTriageSavingWhitelist = true;
      try {
        const aiTriage = await WebpageService.putAiTriageWhitelistedDomainsByFqdn(row.fqdn_id, {
          domains,
        });
        if (aiTriage && typeof aiTriage === 'object') {
          this.$set(this.triageByTabKey[tabKey], 'ai_triage', aiTriage);
          this.applyWhitelistedDomainsFromPack(row, this.triageByTabKey[tabKey]);
        }
        this.$showSuccessMessage('Whitelist saved.');
      } catch (error) {
        if (this.isNoAccessError(error)) {
          this.setWhitelistedDomainsDraftForRow(row, previousDomains);
          this.redirectOnNoAccess();
          return;
        }
        if (this.isWhitelistApiUnavailable(error)) {
          this.setWhitelistedDomainsDraftForRow(row, previousDomains);
          this.$showErrorMessage('Whitelist API is not available yet. Changes were not saved.');
          return;
        }
        this.setWhitelistedDomainsDraftForRow(row, previousDomains);
        this.$showMultiErrorMessage(this.axiosErrorMessages(error));
      } finally {
        this.aiTriageSavingWhitelist = false;
      }
    },
    async applyWhitelistBulkMode(mode, rawDomains) {
      const isAdd = mode === 'add';
      const normalizedDomains = this.normalizeWhitelistedDomains(rawDomains);
      if (normalizedDomains.length === 0) {
        this.$showErrorMessage(isAdd ? 'No valid domains to add.' : 'No domains selected for removal.');
        return;
      }

      // Target every host unconditionally — the server recomputes each host's real diff from
      // its own DB state in one bulk read/write, so the client never needs to guess which hosts
      // would change (guessing from possibly-unloaded local data silently dropped unvisited hosts).
      const fqdnIds = this.fqdnRows
        .map((row) => Number(row.fqdn_id))
        .filter((id) => Number.isFinite(id) && id > 0);
      const selected = this.selectedRow || this.fqdnRows[0];
      if (!selected || selected.fqdn_id == null || fqdnIds.length === 0) {
        this.$showErrorMessage('No target host available for bulk update.');
        return;
      }

      this.aiTriageSavingWhitelist = true;
      try {
        const result = await WebpageService.putAiTriageWhitelistedDomainsByFqdn(selected.fqdn_id, {
          mode,
          domains: normalizedDomains,
          fqdn_ids: fqdnIds,
        });
        const failed = Number(result && result.failed) || 0;
        if (failed > 0) {
          throw new Error(`Bulk whitelist update failed for ${failed} domain(s).`);
        }
        this.fqdnRows.forEach((row) => {
          if (!Array.isArray(row.whitelisted_domains)) {
            return;
          }
          const current = row.whitelisted_domains;
          const next = isAdd
            ? this.normalizeWhitelistedDomains([...current, ...normalizedDomains])
            : current.filter((d) => !normalizedDomains.includes(d));
          this.setWhitelistedDomainsDraftForRow(row, next);
        });
        const updated = Number(result && result.updated) || 0;
        const skippedCount = Number(result && result.skipped) || 0;
        this.$showSuccessMessage(`${isAdd ? 'Added' : 'Removed'} from all domains: ${updated} updated, ${skippedCount} unchanged.`);
      } catch (error) {
        if (this.isNoAccessError(error)) {
          this.redirectOnNoAccess();
        } else if (this.isWhitelistApiUnavailable(error)) {
          this.$showErrorMessage('Whitelist API is not available yet. Changes were not saved.');
        } else {
          this.$showMultiErrorMessage(this.axiosErrorMessages(error));
        }
      } finally {
        this.aiTriageSavingWhitelist = false;
      }
    },
    axiosErrorMessages(error) {
      const data = error && error.response && error.response.data;
      if (data && typeof data.message === 'string') {
        return [data.message];
      }
      if (data && data.errors && typeof data.errors === 'object') {
        const vals = Object.values(data.errors);
        const flat = [];
        vals.forEach((v) => {
          if (Array.isArray(v)) {
            v.forEach((x) => flat.push(String(x)));
          } else {
            flat.push(String(v));
          }
        });
        return flat.filter(Boolean);
      }
      return [(error && error.message) || 'Request failed'];
    },
  },
};
</script>

<style lang="scss" scoped>
.policy-ai-triage-content {
  padding-top: 4px;
}

.fs-12 {
  font-size: 12px;
}

.policy-host-picker {
  display: grid;
  grid-template-columns: 280px 1px minmax(360px, 520px);
  align-items: end;
  column-gap: 14px;
  row-gap: 8px;
  width: fit-content;
  max-width: 100%;
}

.policy-picker-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.policy-picker-group__label {
  font-size: 11px;
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 700;
  color: #8c8c8c;
}

.policy-picker-control {
  height: 36px;
}

.policy-host-picker__divider {
  width: 1px;
  height: 44px;
  background: #e8e8e8;
  margin: 0 2px 2px 0;
}

.policy-host-select {
  width: 280px;
  max-width: 100%;
}

.policy-host-jump-input {
  flex: 1 1 auto;
  min-width: 0;
  max-width: none;
}

.policy-host-jump-wrap {
  display: flex;
  align-items: center;
  width: 100%;
}

.policy-host-jump-btn {
  margin-left: 8px;
  min-width: 68px;
  font-weight: 600;
  height: 36px;
  border-radius: 6px;
}

@media (max-width: 860px) {
  .policy-host-picker {
    grid-template-columns: 1fr;
    width: 100%;
  }

  .policy-host-picker__divider {
    display: none;
  }

  .policy-host-select {
    width: 100%;
  }
}
</style>
